import React, { useState } from "react";
import { submitEOYFeedback } from "../../services/APIClient";

/**
 * Feedback panel for a single report card.
 *
 * Flow:
 *   - Teacher clicks 👍 → record immediately, show "Thanks!"
 *   - Teacher clicks 👎 → show reason textbox, require input before submit
 */
function FeedbackPanel({ studentId, reportText, classId, className }) {
  const [state, setState] = useState({
    rating: null,        // null | "up" | "down"
    showReasonBox: false,
    reasonText: "",
    submitting: false,
    submitted: false,
    error: null,
  });

  const handleThumbsUp = async () => {
    if (state.submitted || state.submitting) return;
    setState((s) => ({ ...s, rating: "up", submitting: true, error: null }));
    try {
      await submitEOYFeedback({
        student_id: studentId,
        report_text: reportText,
        rating: "up",
        class_id: classId || null,
        class_name: className || null,
      });
      setState((s) => ({ ...s, submitting: false, submitted: true }));
    } catch (err) {
      setState((s) => ({
        ...s,
        submitting: false,
        error: "Could not save feedback: " + (err.message || "unknown error"),
      }));
    }
  };

  const handleThumbsDownClick = () => {
    if (state.submitted || state.submitting) return;
    // Don't submit yet — open the reason box
    setState((s) => ({
      ...s,
      rating: "down",
      showReasonBox: true,
      error: null,
    }));
  };

  const handleSubmitReason = async () => {
    if (state.submitting) return;
    if (!state.reasonText.trim()) {
      setState((s) => ({
        ...s,
        error: "Please write a short reason before submitting.",
      }));
      return;
    }
    setState((s) => ({ ...s, submitting: true, error: null }));
    try {
      await submitEOYFeedback({
        student_id: studentId,
        report_text: reportText,
        rating: "down",
        reason: state.reasonText.trim(),
        class_id: classId || null,
        class_name: className || null,
      });
      setState((s) => ({
        ...s,
        submitting: false,
        submitted: true,
        showReasonBox: false,
      }));
    } catch (err) {
      setState((s) => ({
        ...s,
        submitting: false,
        error: "Could not save feedback: " + (err.message || "unknown error"),
      }));
    }
  };

  const handleCancelReason = () => {
    setState((s) => ({
      ...s,
      rating: null,
      showReasonBox: false,
      reasonText: "",
      error: null,
    }));
  };

  // Already submitted → thank you message
  if (state.submitted) {
    return (
      <div className="eoy-feedback">
        <span className="eoy-feedback-thanks">
          ✓ Thanks for your feedback{state.rating === "down" ? " — it helps us improve" : ""}
        </span>
      </div>
    );
  }

  return (
    <div className="eoy-feedback">
      <div className="eoy-feedback-row">
        <span className="eoy-feedback-label">Was this report helpful?</span>
        <button
          type="button"
          className={`eoy-feedback-btn ${state.rating === "up" ? "active" : ""}`}
          onClick={handleThumbsUp}
          disabled={state.submitting || state.showReasonBox}
          aria-label="Thumbs up"
        >
          👍
        </button>
        <button
          type="button"
          className={`eoy-feedback-btn ${state.rating === "down" ? "active" : ""}`}
          onClick={handleThumbsDownClick}
          disabled={state.submitting}
          aria-label="Thumbs down"
        >
          👎
        </button>
        {state.submitting && !state.showReasonBox && (
          <span className="eoy-feedback-saving">Saving…</span>
        )}
      </div>

      {state.showReasonBox && (
        <div className="eoy-reason-box">
          <label className="eoy-reason-label">
            What didn't work about this report?
          </label>
          <textarea
            className="eoy-reason-textarea"
            placeholder="e.g. The wrong task was flagged as lowest, or the tone felt off..."
            value={state.reasonText}
            onChange={(e) =>
              setState((s) => ({ ...s, reasonText: e.target.value }))
            }
            rows={3}
            disabled={state.submitting}
          />
          <div className="eoy-reason-actions">
            <button
              type="button"
              className="eoy-secondary-btn"
              onClick={handleCancelReason}
              disabled={state.submitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="eoy-primary-btn eoy-primary-btn-small"
              onClick={handleSubmitReason}
              disabled={state.submitting || !state.reasonText.trim()}
            >
              {state.submitting ? "Sending..." : "Submit feedback"}
            </button>
          </div>
        </div>
      )}

      {state.error && <div className="eoy-feedback-error">{state.error}</div>}
    </div>
  );
}

export default FeedbackPanel;
